import { z } from "zod";
import { onlyDigits } from "@/lib/format/document";
import { BR_UFS, type BrUf } from "@/lib/format/uf";

export const PROPERTY_TYPES = [
  "house",
  "apartment",
  "penthouse",
  "studio",
  "condo_house",
  "land",
  "farm",
  "commercial_room",
  "store",
  "warehouse",
  "building",
  "garage",
] as const;
export type PropertyType = (typeof PROPERTY_TYPES)[number];

export const PROPERTY_OPERATIONS = ["sale", "rent", "sale_rent"] as const;
export type PropertyOperation = (typeof PROPERTY_OPERATIONS)[number];

export const PROPERTY_STATUSES = [
  "draft",
  "active",
  "paused",
  "sold",
  "rented",
  "archived",
  // Encerramentos sem fechamento via plataforma (corretor perde o imóvel):
  "withdrawn_by_owner",      // proprietário retirou do corretor
  "closed_by_third_party",   // foi vendido/alugado por outro corretor/canal
  "owner_gave_up",           // proprietário desistiu de vender/alugar
] as const;
export type PropertyStatus = (typeof PROPERTY_STATUSES)[number];

/** Estados que indicam que o imóvel não está mais ativo no funil do corretor. */
export const PROPERTY_INACTIVE_STATUSES = [
  "sold",
  "rented",
  "archived",
  "withdrawn_by_owner",
  "closed_by_third_party",
  "owner_gave_up",
] as const satisfies readonly PropertyStatus[];

export function isPropertyClosedByDeal(status: PropertyStatus): boolean {
  return status === "sold" || status === "rented";
}

export const PROPERTY_CATEGORIES = ["residential", "commercial"] as const;
export type PropertyCategory = (typeof PROPERTY_CATEGORIES)[number];

/**
 * Periodicidades permitidas do check-in de disponibilidade (em dias). `0` = desligado
 * (mapeado para `null` no payload). Usadas para montar o select do formulário.
 */
export const AVAILABILITY_CHECK_INTERVALS = [0, 7, 15, 30, 90] as const;
export type AvailabilityCheckInterval = (typeof AVAILABILITY_CHECK_INTERVALS)[number];

export const PROPERTY_FEATURES = [
  "furnished",
  "pet_friendly",
  "accepts_financing",
  "accepts_exchange",
  "pool",
  "barbecue",
  "gym",
  "party_room",
  "playground",
  "sauna",
  "concierge_24h",
  "covered_garage",
  "balcony",
  "backyard",
  "service_area",
  "closet",
  "elevator",
  "air_conditioning",
  "solar_heating",
  "electric_fence",
  "alarm",
  "cctv",
] as const;
export type PropertyFeature = (typeof PROPERTY_FEATURES)[number];

/**
 * Dinheiro como `number` (money-as-number): o `CurrencyInput` digita em
 * centavos e entrega o valor em reais (ex.: 1234.56). Campo em branco chega
 * como `undefined` — nunca como 0.
 */
const moneyOpt = z
  .number()
  .optional()
  .refine((v) => v === undefined || (Number.isFinite(v) && v >= 0), "INVALID_AMOUNT");

/** `publish` = publicar (valida obrigatórios); `draft` = rascunho/pré-cadastro. */
export type FormMode = "draft" | "publish";

const optStr = z
  .string()
  .optional()
  .or(z.literal(""))
  .transform((v) => v || undefined);

/**
 * Endereço. Em `publish` (publicar) o CEP, bairro, cidade e UF são obrigatórios.
 * Em `draft` (rascunho/pré-cadastro) tudo é opcional, mas o que estiver
 * preenchido ainda precisa ter formato válido (CEP 8 dígitos, UF existente).
 */
function buildAddressSchema(mode: FormMode) {
  const publish = mode === "publish";
  return z.object({
    zipcode: publish
      ? z
          .string()
          .min(1, "ZIPCODE_REQUIRED")
          .transform((v) => onlyDigits(v))
          .refine((v) => v.length === 8, "INVALID_ZIPCODE")
      : z
          .string()
          .optional()
          .transform((v) => onlyDigits(v ?? ""))
          .refine((v) => v === "" || v.length === 8, "INVALID_ZIPCODE"),
    street: optStr,
    number: optStr,
    complement: optStr,
    neighborhood: publish ? z.string().min(1, "NEIGHBORHOOD_REQUIRED") : optStr,
    city: publish ? z.string().min(1, "CITY_REQUIRED") : optStr,
    state: publish
      ? z
          .string()
          .toUpperCase()
          .refine((v) => (BR_UFS as readonly string[]).includes(v), "INVALID_UF")
          .transform((v) => v as BrUf)
      : z
          .string()
          .optional()
          .transform((v) => (v ?? "").toUpperCase())
          .refine((v) => v === "" || (BR_UFS as readonly string[]).includes(v), "INVALID_UF"),
    hideExactAddress: z.boolean().optional().default(false),
  });
}

const intOpt = z
  .string()
  .optional()
  .or(z.literal(""))
  .transform((v) => (v ? Number.parseInt(v, 10) : undefined))
  .refine((v) => v === undefined || (Number.isFinite(v) && v >= 0), "INVALID_NUMBER");

const areaOpt = z
  .string()
  .optional()
  .or(z.literal(""))
  .transform((v) => {
    if (!v) return undefined;
    const n = Number(v.replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  })
  .refine((v) => v === undefined || v >= 0, "INVALID_AREA");

function buildPropertySchema(mode: FormMode) {
  const publish = mode === "publish";
  return z
    .object({
      title: publish
        ? z.string().min(5, "TITLE_TOO_SHORT").max(200, "TITLE_TOO_LONG")
        : z
            .string()
            .max(200, "TITLE_TOO_LONG")
            .optional()
            .or(z.literal(""))
            .transform((v) => v ?? ""),
      type: z.enum(PROPERTY_TYPES),
      category: z.enum(PROPERTY_CATEGORIES),
      operation: z.enum(PROPERTY_OPERATIONS),
      status: z.enum(PROPERTY_STATUSES).optional().default("active"),

      // Periodicidade do check-in de disponibilidade. O select entrega uma string
      // ("0"|"7"|"15"|"30"|"90"); 0/vazio vira null (desligado) no payload.
      availabilityCheckIntervalDays: z
        .union([z.string(), z.number(), z.null()])
        .optional()
        .transform((v) => {
          const n =
            typeof v === "number"
              ? v
              : typeof v === "string" && v
                ? Number.parseInt(v, 10)
                : 0;
          return Number.isFinite(n) && n > 0 ? n : null;
        }),

      salePrice: moneyOpt,
    rentPrice: moneyOpt,
    condoFee: moneyOpt,
    iptuYearly: moneyOpt,

    bedrooms: intOpt,
    suites: intOpt,
    bathrooms: intOpt,
    parkingSpots: intOpt,
    builtArea: areaOpt,
    totalArea: areaOpt,
    floor: intOpt,

    // Medidas do TERRENO (só para type=land), em metros.
    landFront: areaOpt,
    landDepth: areaOpt,
    landRear: areaOpt,

    features: z.array(z.enum(PROPERTY_FEATURES)).default([]),

    description: z
      .string()
      .max(8000, "DESCRIPTION_TOO_LONG")
      .optional()
      .or(z.literal(""))
      .transform((v) => v || undefined),

    ownerId: publish
      ? z.string().min(1, "OWNER_REQUIRED")
      : z
          .string()
          .optional()
          .transform((v) => v ?? ""),
    address: buildAddressSchema(mode),

    videoUrl: z
      .string()
      .optional()
      .or(z.literal(""))
      .transform((v) => v || undefined)
      .refine((v) => !v || /^https?:\/\//.test(v), "INVALID_URL"),
    virtualTourUrl: z
      .string()
      .optional()
      .or(z.literal(""))
      .transform((v) => v || undefined)
      .refine((v) => !v || /^https?:\/\//.test(v), "INVALID_URL"),

    isCondominium: z.boolean().optional().default(false),
    condominiumName: z
      .string()
      .max(200, "TOO_LONG")
      .optional()
      .or(z.literal(""))
      .transform((v) => v || undefined),

    isManaged: z.boolean().optional().default(false),
    managementFeePercent: z
      .string()
      .optional()
      .or(z.literal(""))
      .transform((v) => (v ? Number(v.replace(",", ".")) : undefined))
      .refine(
        (v) => v === undefined || (Number.isFinite(v) && v >= 0 && v <= 100),
        "INVALID_PERCENT",
      ),
    saleCommissionPercent: z
      .string()
      .optional()
      .or(z.literal(""))
      .transform((v) => (v ? Number(v.replace(",", ".")) : undefined))
      .refine(
        (v) => v === undefined || (Number.isFinite(v) && v >= 0 && v <= 100),
        "INVALID_PERCENT",
      ),
    rentCommissionPercent: z
      .string()
      .optional()
      .or(z.literal(""))
      .transform((v) => (v ? Number(v.replace(",", ".")) : undefined))
      .refine(
        (v) => v === undefined || (Number.isFinite(v) && v >= 0 && v <= 100),
        "INVALID_PERCENT",
      ),
    })
    .superRefine((data, ctx) => {
      // Condomínio marcado exige o nome (independe de publicar/rascunho).
      if (data.isCondominium && !data.condominiumName) {
        ctx.addIssue({
          code: "custom",
          path: ["condominiumName"],
          message: "CONDOMINIUM_NAME_REQUIRED",
        });
      }
      // Preço por operação só é obrigatório ao publicar. Rascunho não exige.
      if (!publish) return;
      const needsSale = data.operation === "sale" || data.operation === "sale_rent";
      const needsRent = data.operation === "rent" || data.operation === "sale_rent";
      if (needsSale && !data.salePrice) {
        ctx.addIssue({
          code: "custom",
          path: ["salePrice"],
          message: "SALE_PRICE_REQUIRED",
        });
      }
      if (needsRent && !data.rentPrice) {
        ctx.addIssue({
          code: "custom",
          path: ["rentPrice"],
          message: "RENT_PRICE_REQUIRED",
        });
      }
    });
}

/** Schema estrito para PUBLICAR: exige título, dono, endereço e preço. */
export const propertySchema = buildPropertySchema("publish");
/** Schema relaxado para RASCUNHO/pré-cadastro: nada obrigatório (só formato). */
export const draftPropertySchema = buildPropertySchema("draft");

export type PropertyInput = z.input<typeof propertySchema>;
export type PropertyOutput = z.output<typeof propertySchema>;
